'use client';

import { PlanData, PlanPeriod, ActionResult } from 'components';
import { JSX, useCallback } from 'react';

import { Button, Text } from '@videoblocks/design-system';

import { PlanCardHeader } from './PlanCardHeader';
import { PlanFeatureList } from './PlanFeatureList';

export interface PlanCardProps {
    plan: PlanData;
    selectedPeriod: PlanPeriod;
    currencySymbol: string;
    subscriptionId: string;
    isCancelledOrExpired: boolean;
    onStatusUpdate: (result: ActionResult) => void;
    onOpenModal: () => void;
}

const PlanBadge = ({ text }: { text: string }) => {
    return (
        <Text fontSize="sm" fontWeight="semibold" className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-sm">
            {text}
        </Text>
    );
};

const LicenseSvg = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <g clipPath="url(#clip0_2012_2314)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1.08997 10.9799V1.01991H5.97997H5.98147C5.98404 1.02091 5.98655 1.02208 5.98897 1.02341L6.00397 1.03741L8.39097 3.18091C8.43962 3.22597 8.49674 3.2609 8.55902 3.28368C8.6213 3.30645 8.68749 3.31661 8.75373 3.31356C8.81998 3.3105 8.88495 3.29431 8.94487 3.26591C9.0048 3.2375 9.05847 3.19747 9.10277 3.14812C9.14708 3.09878 9.18112 3.04112 9.20293 2.97849C9.22473 2.91587 9.23386 2.84953 9.22978 2.78334C9.22571 2.71716 9.2085 2.65244 9.17918 2.59297C9.14985 2.53349 9.10899 2.48044 9.05897 2.43691L6.67997 0.300909C6.4952 0.125444 6.25171 0.0250872 5.99697 0.0194092H1.07697C0.816466 0.0199092 0.561466 0.108409 0.369966 0.299909C0.178466 0.491909 0.0899658 0.747409 0.0899658 1.00691V10.9939C0.0899658 11.2539 0.178466 11.5089 0.369966 11.7004C0.561466 11.8919 0.816966 11.9804 1.07647 11.9804H5.94797C6.08057 11.9804 6.20775 11.9277 6.30152 11.834C6.39529 11.7402 6.44797 11.613 6.44797 11.4804C6.44797 11.3478 6.39529 11.2206 6.30152 11.1269C6.20775 11.0331 6.08057 10.9804 5.94797 10.9804H1.08997V10.9799ZM7.54547 11.4849L7.88997 8.92191C8.32797 9.17191 8.83497 9.31491 9.37497 9.31491C9.89593 9.31572 10.4081 9.18023 10.8605 8.92191L11.205 11.4849C11.26 11.9324 10.7615 12.1564 10.484 11.8764L9.37497 10.7574L8.26647 11.8764C8.19965 11.9367 8.11577 11.9748 8.02639 11.9854C7.93702 11.9961 7.84655 11.9787 7.76745 11.9358C7.68836 11.8928 7.62453 11.8264 7.58477 11.7456C7.54501 11.6649 7.53128 11.5738 7.54547 11.4849ZM2.23297 3.68591C2.23297 3.5533 2.28564 3.42612 2.37941 3.33236C2.47318 3.23859 2.60036 3.18591 2.73297 3.18591H5.94797C6.08057 3.18591 6.20775 3.23859 6.30152 3.33236C6.39529 3.42612 6.44797 3.5533 6.44797 3.68591C6.44797 3.81852 6.39529 3.94569 6.30152 4.03946C6.20775 4.13323 6.08057 4.18591 5.94797 4.18591H2.73297C2.60036 4.18591 2.47318 4.13323 2.37941 4.03946C2.28564 3.94569 2.23297 3.81852 2.23297 3.68591ZM2.73297 5.62191C2.60036 5.62191 2.47318 5.67459 2.37941 5.76836C2.28564 5.86212 2.23297 5.9893 2.23297 6.12191C2.23297 6.25452 2.28564 6.3817 2.37941 6.47546C2.47318 6.56923 2.60036 6.62191 2.73297 6.62191H5.94797C6.08057 6.62191 6.20775 6.56923 6.30152 6.47546C6.39529 6.3817 6.44797 6.25452 6.44797 6.12191C6.44797 5.9893 6.39529 5.86212 6.30152 5.76836C6.20775 5.67459 6.08057 5.62191 5.94797 5.62191H2.73297ZM2.23297 8.55741C2.23297 8.4248 2.28564 8.29763 2.37941 8.20386C2.47318 8.11009 2.60036 8.05741 2.73297 8.05741H5.16897C5.30157 8.05741 5.42875 8.11009 5.52252 8.20386C5.61629 8.29763 5.66897 8.4248 5.66897 8.55741C5.66897 8.69002 5.61629 8.8172 5.52252 8.91096C5.42875 9.00473 5.30157 9.05741 5.16897 9.05741H2.73297C2.60036 9.05741 2.47318 9.00473 2.37941 8.91096C2.28564 8.8172 2.23297 8.69002 2.23297 8.55741ZM9.37497 8.56491C9.9717 8.56491 10.544 8.32786 10.966 7.9059C11.3879 7.48394 11.625 6.91165 11.625 6.31491C11.625 5.71817 11.3879 5.14588 10.966 4.72392C10.544 4.30196 9.9717 4.06491 9.37497 4.06491C8.77823 4.06491 8.20593 4.30196 7.78398 4.72392C7.36202 5.14588 7.12497 5.71817 7.12497 6.31491C7.12497 6.91165 7.36202 7.48394 7.78398 7.9059C8.20593 8.32786 8.77823 8.56491 9.37497 8.56491ZM9.37497 7.02441C9.56314 7.02441 9.7436 6.94966 9.87666 6.8166C10.0097 6.68355 10.0845 6.50308 10.0845 6.31491C10.0845 6.12674 10.0097 5.94627 9.87666 5.81322C9.7436 5.68016 9.56314 5.60541 9.37497 5.60541C9.18679 5.60541 9.00633 5.68016 8.87327 5.81322C8.74022 5.94627 8.66547 6.12674 8.66547 6.31491C8.66547 6.50308 8.74022 6.68355 8.87327 6.8166C9.00633 6.94966 9.18679 7.02441 9.37497 7.02441Z"
                    fill="#1F2830"
                />
            </g>
            <defs>
                <clipPath id="clip0_2012_2314">
                    <rect width="12" height="12" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

export const PlanCard = ({
    plan,
    selectedPeriod,
    currencySymbol,
    subscriptionId,
    isCancelledOrExpired,
    onStatusUpdate,
    onOpenModal,
}: PlanCardProps): JSX.Element => {
    const isCurrentPeriodPlan =
        selectedPeriod === 'monthly' ? plan.currentUserPlan.isCurrentMonthly : plan.currentUserPlan.isCurrentAnnual;

    const handleUpgrade = useCallback(() => {
        // Allow upgrade for cancelled/expired subscriptions even if it's current plan
        if (isCurrentPeriodPlan && !isCancelledOrExpired) return;
        onOpenModal();
    }, [isCurrentPeriodPlan, isCancelledOrExpired, onOpenModal]);

    return (
        <div
            className={`relative flex flex-col px-6 py-8 gap-4 bg-white rounded-2xl border border-solid border-gray-300 text-gray-900 text-sm w-full md:flex-shrink-0 ${currencySymbol === '$' ? 'md:w-[296px]' : 'md:w-[320px]'}`}
        >
            {isCurrentPeriodPlan && (
                <div className="absolute -top-3 left-8">
                    <PlanBadge text="CURRENT PLAN" />
                </div>
            )}
            {plan.isBestValue && selectedPeriod === 'annual' && !isCurrentPeriodPlan && (
                <div className="absolute -top-3 left-8">
                    <PlanBadge text="BEST VALUE" />
                </div>
            )}

            <PlanCardHeader plan={plan} selectedPeriod={selectedPeriod} currencySymbol={currencySymbol} />

            <div className="w-full">
                <Button
                    variant={isCurrentPeriodPlan && !isCancelledOrExpired ? 'outlined-dark' : 'primary'}
                    disabled={isCurrentPeriodPlan && !isCancelledOrExpired}
                    onClick={handleUpgrade}
                    className="w-full h-12"
                >
                    {isCancelledOrExpired && isCurrentPeriodPlan
                        ? 'Restart Plan'
                        : isCurrentPeriodPlan
                          ? 'Current Plan'
                          : 'Change Plan'}
                </Button>
            </div>

            <div className="flex items-center gap-2 px-0">
                <LicenseSvg />
                <Text
                    asChild
                    fontSize="sm"
                    fontWeight="semibold"
                    className="underline decoration-solid underline-offset-auto"
                >
                    <a href="/business-solutions/license-comparison" target="_blank" rel="noopener noreferrer">
                        {plan.licenseType}
                    </a>
                </Text>
            </div>

            <PlanFeatureList plan={plan} />
        </div>
    );
};
